import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useLocation } from 'react-router-dom';
import { API_URL } from '../config/api';

interface ImpersonationState {
  isImpersonating: boolean;
  impersonatedUser: {
    id: string;
    email?: string;
    name?: string;
  } | null;
  impersonationToken: string | null;
}

interface ImpersonationContextType {
  impersonationState: ImpersonationState;
  startImpersonation: (organizationId: string, memberId: string) => Promise<void>;
  exitImpersonation: () => Promise<void>;
  getAuthToken: () => Promise<string | null>;
  getEffectiveUserId: () => string | null; // Returns impersonated user ID if impersonating, otherwise null (caller should use Clerk userId)
}

const ImpersonationContext = createContext<ImpersonationContextType | undefined>(undefined);

const IMPERSONATION_STORAGE_KEY = 'impersonation_state';

// Provider implementation that uses Clerk (for authenticated pages)
// This component should ONLY be rendered when ClerkProvider is available
const ImpersonationProviderWithClerk: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { getToken } = useAuth();
  const [impersonationState, setImpersonationState] = useState<ImpersonationState>({
    isImpersonating: false,
    impersonatedUser: null,
    impersonationToken: null
  });

  // Load impersonation state from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem(IMPERSONATION_STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setImpersonationState(parsed);
      } catch (error) {
        console.error('Error loading impersonation state:', error);
        localStorage.removeItem(IMPERSONATION_STORAGE_KEY);
      }
    }
  }, []);

  // Save impersonation state to localStorage whenever it changes
  useEffect(() => {
    if (impersonationState.isImpersonating) {
      localStorage.setItem(IMPERSONATION_STORAGE_KEY, JSON.stringify(impersonationState));
    } else {
      localStorage.removeItem(IMPERSONATION_STORAGE_KEY);
    }
  }, [impersonationState]);

  const startImpersonation = async (organizationId: string, memberId: string) => {
    try {
      const token = await getToken();
      if (!token) {
        throw new Error('Authentication token not available');
      }

      const response = await fetch(
        `${API_URL}/api/organizations/${organizationId}/members/${memberId}/impersonate`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to start impersonation' }));
        throw new Error(errorData.error || 'Failed to start impersonation');
      }

      const data = await response.json();
      
      setImpersonationState({
        isImpersonating: true,
        impersonatedUser: data.impersonatedUser,
        impersonationToken: data.impersonationToken
      });
    } catch (error: any) {
      console.error('Error starting impersonation:', error);
      throw error;
    }
  };

  const exitImpersonation = async () => {
    console.log('🚪 exitImpersonation called');
    
    // IMMEDIATELY clear localStorage to prevent reload from restoring state
    console.log('🗑️ Clearing localStorage immediately...');
    localStorage.removeItem(IMPERSONATION_STORAGE_KEY);
    
    // Clear state immediately (don't wait for API)
    setImpersonationState({
      isImpersonating: false,
      impersonatedUser: null,
      impersonationToken: null
    });
    
    // Try to call API, but don't wait for it - reload immediately
    console.log('🚪 Attempting exit-impersonation API call (non-blocking):', `${API_URL}/api/admin/exit-impersonation`);
    
    // Fire and forget - don't wait for response
    getToken()
      .then(token => {
        if (token) {
          // Use AbortController with timeout to prevent hanging
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 2000); // 2 second timeout
          
          fetch(`${API_URL}/api/admin/exit-impersonation`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`
            },
            signal: controller.signal
          })
            .then(response => {
              clearTimeout(timeoutId);
              if (response.ok) {
                console.log('✅ Exit impersonation API call successful');
              } else {
                console.warn('⚠️ Exit impersonation API returned non-OK status:', response.status);
              }
            })
            .catch(error => {
              clearTimeout(timeoutId);
              if (error.name === 'AbortError') {
                console.warn('⏱️ Exit impersonation API call timed out (non-critical)');
              } else {
                console.warn('⚠️ Exit impersonation API call failed (non-critical):', error.message);
              }
            });
        }
      })
      .catch(error => {
        console.warn('⚠️ Could not get token for API call (non-critical):', error);
      });
    
    // Reload immediately - don't wait for API
    console.log('🔄 Reloading page immediately...');
    setTimeout(() => {
      window.location.reload();
    }, 100); // Small delay to ensure state is cleared
  };

  const getAuthToken = async (): Promise<string | null> => {
    if (impersonationState.isImpersonating && impersonationState.impersonationToken) {
      return impersonationState.impersonationToken;
    }
    return await getToken();
  };

  const getEffectiveUserId = (): string | null => {
    if (impersonationState.isImpersonating && impersonationState.impersonatedUser) {
      return impersonationState.impersonatedUser.id;
    }
    return null; // Return null to indicate using Clerk userId
  };

  return (
    <ImpersonationContext.Provider
      value={{
        impersonationState,
        startImpersonation,
        exitImpersonation,
        getAuthToken,
        getEffectiveUserId
      }}
    >
      {children}
    </ImpersonationContext.Provider>
  );
};

// Provider implementation without Clerk (for public card pages)
const ImpersonationProviderWithoutClerk: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [impersonationState, setImpersonationState] = useState<ImpersonationState>({
    isImpersonating: false,
    impersonatedUser: null,
    impersonationToken: null
  });

  const startImpersonation = async (organizationId: string, memberId: string) => {
    throw new Error('Impersonation is not available on public card pages');
  };

  const exitImpersonation = async () => {
    // No-op on public pages
  };

  const getAuthToken = async (): Promise<string | null> => {
    return null;
  };

  const getEffectiveUserId = (): string | null => {
    return null;
  };

  return (
    <ImpersonationContext.Provider
      value={{
        impersonationState,
        startImpersonation,
        exitImpersonation,
        getAuthToken,
        getEffectiveUserId
      }}
    >
      {children}
    </ImpersonationContext.Provider>
  );
};

// Main provider that conditionally renders the appropriate implementation
export const ImpersonationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const location = useLocation();
  const isPublicCardPage = location.pathname.startsWith('/card/');

  if (isPublicCardPage) {
    return <ImpersonationProviderWithoutClerk>{children}</ImpersonationProviderWithoutClerk>;
  }

  return <ImpersonationProviderWithClerk>{children}</ImpersonationProviderWithClerk>;
};

export const useImpersonation = () => {
  const context = useContext(ImpersonationContext);
  if (context === undefined) {
    throw new Error('useImpersonation must be used within an ImpersonationProvider');
  }
  return context;
};

